import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatAPI } from '../services/api';
import './Chat.css';

function Chat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (chatId) {
      fetchChat(chatId);
    }
  }, [chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [activeChat?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async () => {
    try {
      const response = await chatAPI.getAll();
      setChats(response.data.chats);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChat = async (id) => {
    try {
      const response = await chatAPI.getById(id);
      setActiveChat(response.data.chat);
    } catch (error) {
      console.error('Error fetching chat:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !activeChat || sending) return;

    setSending(true);
    try {
      const response = await chatAPI.sendMessage(activeChat._id, message);
      setActiveChat(response.data.chat);
      setMessage('');
      fetchChats();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const selectChat = (chat) => {
    navigate(`/chat/${chat._id}`);
  };

  const getOtherParticipant = (chat) => {
    return chat.participants.find(p => p._id !== currentUser?.id);
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;

    if (diff < 86400000) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return d.toLocaleDateString([], { weekday: 'short' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!currentUser) {
    return (
      <div className="chat-page">
        <div className="chat-login-prompt">
          <h2>Please log in to access messages</h2>
          <button onClick={() => navigate('/login')} className="btn btn-primary">
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page">
      <div className="chat-container">
        {/* Chat List Sidebar */}
        <div className={`chat-sidebar ${activeChat ? 'hide-mobile' : ''}`}>
          <div className="chat-sidebar-header">
            <h2>Messages</h2>
          </div>

          {loading ? (
            <div className="chat-loading">Loading conversations...</div>
          ) : chats.length === 0 ? (
            <div className="no-chats">
              <p>No conversations yet</p>
              <p className="hint">Start chatting from College Zone!</p>
            </div>
          ) : (
            <div className="chat-list">
              {chats.map(chat => {
                const other = getOtherParticipant(chat);
                return (
                  <div
                    key={chat._id}
                    className={`chat-list-item ${activeChat?._id === chat._id ? 'active' : ''}`}
                    onClick={() => selectChat(chat)}
                  >
                    <div className="chat-avatar">
                      {other?.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="chat-preview">
                      <div className="chat-name">{other?.name || 'Unknown User'}</div>
                      <div className="chat-last-message">
                        {chat.lastMessage?.content
                          ? chat.lastMessage.content.substring(0, 40) + (chat.lastMessage.content.length > 40 ? '...' : '')
                          : 'No messages yet'}
                      </div>
                    </div>
                    {chat.lastMessage?.createdAt && (
                      <div className="chat-time">
                        {formatTime(chat.lastMessage.createdAt)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Active Chat */}
        <div className={`chat-main ${!activeChat ? 'hide-mobile' : ''}`}>
          {activeChat ? (
            <>
              <div className="chat-header">
                <button className="back-btn mobile-only" onClick={() => navigate('/chat')}>
                  <span>&larr;</span>
                </button>
                <div className="chat-header-info">
                  <div className="chat-header-avatar">
                    {getOtherParticipant(activeChat)?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3>{getOtherParticipant(activeChat)?.name || 'Unknown'}</h3>
                    <span className="online-status">Online</span>
                  </div>
                </div>
              </div>

              <div className="chat-messages">
                {activeChat.messages.length === 0 ? (
                  <div className="no-messages">
                    <p>No messages yet. Say hello!</p>
                  </div>
                ) : (
                  activeChat.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`message ${msg.sender._id === currentUser.id ? 'sent' : 'received'}`}
                    >
                      <div className="message-content">{msg.content}</div>
                      <div className="message-time">
                        {formatTime(msg.createdAt)}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form className="chat-input-form" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="chat-input"
                  disabled={sending}
                />
                <button type="submit" className="send-btn" disabled={sending || !message.trim()}>
                  {sending ? '...' : 'Send'}
                </button>
              </form>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="no-chat-icon">💬</div>
              <h3>Select a conversation</h3>
              <p>Choose from your existing conversations or start a new one from College Zone</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Chat;
